import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './index.css';

const BsyTab = ({ tabs, defaultActiveKey, onChange, className }) => {
  const [activeKey, setActiveKey] = useState(defaultActiveKey || tabs[0]?.key);
  const [tabList, setTabList] = useState(tabs);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabListRef = useRef(null);
  const tabsContainerRef = useRef(null);

  const handleTabClick = (key) => {
    setActiveKey(key);
    onChange?.(key);
    scrollToTab(key);
  };

  const scrollToTab = (key) => {
    const tabElement = document.querySelector(`[data-key="${key}"]`);
    if (tabElement && tabListRef.current) {
      const container = tabListRef.current;
      const tabLeft = tabElement.offsetLeft;
      const tabWidth = tabElement.offsetWidth;
      const containerWidth = container.offsetWidth;

      // 计算标签应该滚动到的位置，使其居中显示
      const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);

      // 确保不会滚动超出范围
      const maxScroll = container.scrollWidth - containerWidth;
      const finalScroll = Math.max(0, Math.min(scrollPosition, maxScroll));

      container.scrollTo({
        left: finalScroll,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (tabListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scroll = (direction) => {
    if (tabListRef.current) {
      const scrollAmount = 200;
      const container = tabListRef.current;
      const newScrollLeft = direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const tabList = tabListRef.current;
    if (tabList) {
      handleScroll();
      tabList.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (tabList) {
        tabList.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    setTabList(tabs)
    handleScroll();
  }, [tabs]);

  useEffect(() => {
    setActiveKey(defaultActiveKey)
    scrollToTab(defaultActiveKey);
  }, [defaultActiveKey]);

  return (
    <div className={`bsy-tab ${className || ''}`} ref={tabsContainerRef}>
      <div className="bsy-tab-header-container">
        {showLeftArrow && (
          <div className="bsy-tab-arrow bsy-tab-arrow-left" onClick={() => scroll('left')}>
            <i className="icon-bsy-left"></i>
          </div>
        )}
        <div className="bsy-tab-list" ref={tabListRef}>
          {tabList.map((tab) => (
            <div
              key={tab.key}
              data-key={tab.key}
              className={`bsy-tab-item ${activeKey === tab.key ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.icon && <span className="bsy-tab-icon">{tab.icon}</span>}
              <span className="bsy-tab-title">{tab.title}</span>
            </div>
          ))}
        </div>
        {showRightArrow && (
          <div className="bsy-tab-arrow bsy-tab-arrow-right" onClick={() => scroll('right')}>
            <i className="icon-bsy-right"></i>
          </div>
        )}
      </div>
      <div className="bsy-tab-content">
        {tabs.find((tab) => tab.key === activeKey)?.content}
      </div>
    </div>
  );
};

BsyTab.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.node,
      content: PropTypes.node,
    })
  ).isRequired,
  defaultActiveKey: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default BsyTab;
